import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { companyInfo } from '../utils/dataInfo';
import { formatCreatedAtDate } from '../utils/helpers';

// Safe version of getCategoriesNameById that handles errors
const getCategoriesNameById = (id, branchNamesFilterData) => {
    try {
        // Check if branchNamesFilterData is valid
        if (!branchNamesFilterData || !Array.isArray(branchNamesFilterData)) {
            return 'N/A';
        }
        
        // Find the branch
        const branch = branchNamesFilterData.find(b => b.id == id);
        return branch ? `${branch.name} (${branch.code})` : 'N/A';
    } catch (error) {
        console.error('Error in getCategoriesNameById:', error);
        return 'N/A';
    }
};

// Helper function to get status name
const getStatusName = (status) => {
    return status === 1 ? 'Active' : 'Inactive';
};

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
    } catch (error) {
        return 'N/A';
    }
};

// Helper function to split text into multiple lines
const splitTextIntoLines = (doc, text, maxWidth) => {
    if (!text) return [''];
    const words = text.split(' ');
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const width = doc.getTextWidth(currentLine + ' ' + word);
        if (width < maxWidth) {
            currentLine += ' ' + word;
        } else {
            lines.push(currentLine);
            currentLine = word;
        }
    }
    lines.push(currentLine);
    return lines;
};

// Manual table drawing function for PDF - Only Date and Customer Type Name
const drawCustomerTypesTable = (doc, data, startY) => {
    const pageWidth = doc.internal.pageSize.width;
    const margin = 14;
    const rowHeight = 12;

    // Column widths: #, Created Date, Customer Type Name
    const colWidths = [10, 40, 140]; // # (10), Date (40), Name (140)

    // Check if we need a new page
    let currentY = startY;
    const neededHeight = (data.length + 1) * rowHeight;
    if (currentY + neededHeight > doc.internal.pageSize.height - 20) {
        doc.addPage();
        currentY = margin;
    }

    // Draw table header
    doc.setFillColor(255, 255, 255);
    doc.setTextColor(25, 118, 210);
    doc.setFont(undefined, 'bold');

    const headers = ['#', 'Created Date', 'Customer Type Name'];
    let xPos = margin;

    // Draw header background
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

    // Draw header text
    headers.forEach((header, index) => {
        const cellX = xPos + 2;
        doc.text(header, cellX, currentY + 7);
        xPos += colWidths[index];
    });

    // Draw header underline
    doc.setDrawColor(25, 118, 210);
    doc.setLineWidth(0.5);
    doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);

    currentY += rowHeight;

    // Draw table rows
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);

    data.forEach((row, rowIndex) => {
        let maxLines = 1;
        const cellLines = row.map((cell, cellIndex) => {
            const text = String(cell);
            const maxWidth = colWidths[cellIndex] - 4;
            const lines = splitTextIntoLines(doc, text, maxWidth);
            maxLines = Math.max(maxLines, lines.length);
            return lines;
        });

        const actualRowHeight = rowHeight * maxLines;

        if (currentY + actualRowHeight > doc.internal.pageSize.height - 20) {
            doc.addPage();
            currentY = margin;
            
            doc.setFillColor(255, 255, 255);
            doc.setTextColor(25, 118, 210);
            doc.setFont(undefined, 'bold');
            
            doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
            
            xPos = margin;
            headers.forEach((header, index) => {
                doc.text(header, xPos + 2, currentY + 7);
                xPos += colWidths[index];
            });
            
            doc.setDrawColor(25, 118, 210);
            doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
            
            currentY += rowHeight;
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);
        }
        
        if (rowIndex % 2 === 0) {
            doc.setFillColor(248, 248, 248);
        } else {
            doc.setFillColor(255, 255, 255);
        }
        
        doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
        
        cellLines.forEach((lines, cellIndex) => {
            xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
            
            lines.forEach((line, lineIndex) => {
                const lineY = currentY + 7 + (lineIndex * rowHeight);
                doc.text(line, xPos + 2, lineY);
            });
        });
        
        currentY += actualRowHeight;
    });

    return currentY;
};

// Client-side Excel export for Customer Types - Show all attributes
export const exportCustomerTypesToExcel = (customerTypes, branchNamesFilterData = [], filters = {}) => {
    try {
        // Ensure customerTypes is an array
        if (!Array.isArray(customerTypes)) {
            throw new Error('Customer Types data is not an array');
        }

        // Ensure branchNamesFilterData is an array
        const safeBranchData = Array.isArray(branchNamesFilterData) ? branchNamesFilterData : [];

        // Prepare data for Excel with all attributes
        const excelData = customerTypes.map((customerType, index) => {
            // Safely handle customer type data
            const safeCustomerType = customerType || {};
            
            return {
                'No.': index + 1,
                'ID': safeCustomerType.id || 'N/A',
                'Customer Type Name': safeCustomerType.name || 'N/A',
                'Description': safeCustomerType.description || 'N/A',
                'Status': getStatusName(safeCustomerType.status),
                'Branch': getCategoriesNameById(safeCustomerType.branch_id, safeBranchData),
                'Created Date': formatDate(safeCustomerType.created_at),
                'Updated Date': formatDate(safeCustomerType.updated_at)
            };
        });

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Auto-size columns
        const maxWidth = excelData.reduce((widths, row) => {
            Object.keys(row).forEach((key, idx) => {
                const length = String(row[key]).length;
                if (!widths[idx] || length > widths[idx]) {
                    widths[idx] = length;
                }
            });
            return widths;
        }, []);
        
        worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Customer Types');

        // Generate Excel file and download
        const fileName = `customer_types_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Customer Types Excel export failed:', error);
        throw new Error('Failed to export to Excel: ' + error.message);
    }
};

// PDF export for Customer Types - Show only Created Date and Customer Type Name
export const exportCustomerTypesToPDFEnhanced = (customerTypes, branchNamesFilterData = [], filters = {}) => {
    try {
        // Ensure customerTypes is an array
        if (!Array.isArray(customerTypes)) {
            throw new Error('Customer Types data is not an array');
        }

        const doc = new jsPDF();
        
        // Set document properties
        doc.setProperties({
            title: 'Customer Types Report',
            subject: 'Customer Types Export',
            author: companyInfo.name,
            keywords: 'customer types, export, report',
            creator: companyInfo.name
        });
        
        // Title section
        doc.setFontSize(20);
        doc.setTextColor(25, 118, 210);
        doc.text('CUSTOMER TYPES REPORT', 105, 25, { align: 'center' });
        
        // Generation details
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        let currentY = 35;
        doc.text(`Report Generated: ${new Date().toLocaleString()}`, 14, currentY);
        currentY += 6;
        doc.text(`Total Customer Types: ${customerTypes.length}`, 14, currentY);
        currentY += 6;
        
        // Filters applied
        if (filters && (filters.name || filters.searchTerm || filters.dateFrom || filters.dateTo)) {
            doc.text('Applied Filters:', 14, currentY);
            currentY += 6;
            
            if (filters.name) {
                doc.text(`• Name: ${filters.name}`, 20, currentY);
                currentY += 6;
            }
            if (filters.searchTerm) {
                doc.text(`• Search: ${filters.searchTerm}`, 20, currentY);
                currentY += 6;
            }
            if (filters.dateFrom) {
                doc.text(`• From date: ${new Date(filters.dateFrom).toLocaleDateString()}`, 20, currentY);
                currentY += 6;
            }
            if (filters.dateTo) {
                doc.text(`• To date: ${new Date(filters.dateTo).toLocaleDateString()}`, 20, currentY);
                currentY += 6;
            }
            currentY += 6;
        }
        
        // Prepare table data with only: #, Created Date, Customer Type Name
        const tableData = customerTypes.map((customerType, index) => {
            const safeCustomerType = customerType || {};
            return [
                (index + 1).toString(),
                formatDate(safeCustomerType.created_at),
                safeCustomerType.name || 'N/A'
            ];
        });
        
        // Draw the table
        drawCustomerTypesTable(doc, tableData, currentY + 5);
        
        const fileName = `customer_types_report_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Customer Types PDF export failed:', error);
        throw new Error('Failed to export to PDF: ' + error.message);
    }
};

// CSV export for Customer Types - Show all attributes
export const exportCustomerTypesToCSV = (customerTypes, branchNamesFilterData = []) => {
    try {
        // Ensure customerTypes is an array
        if (!Array.isArray(customerTypes)) {
            throw new Error('Customer Types data is not an array');
        }

        // Ensure branchNamesFilterData is an array
        const safeBranchData = Array.isArray(branchNamesFilterData) ? branchNamesFilterData : [];

        const headers = ['ID', 'Customer Type Name', 'Description', 'Status', 'Branch', 'Created Date', 'Updated Date'];
        
        const csvData = customerTypes.map(customerType => {
            const safeCustomerType = customerType || {};
            return [
                safeCustomerType.id || '',
                `"${(safeCustomerType.name || '').replace(/"/g, '""')}"`,
                `"${(safeCustomerType.description || '').replace(/"/g, '""')}"`,
                `"${getStatusName(safeCustomerType.status).replace(/"/g, '""')}"`,
                `"${getCategoriesNameById(safeCustomerType.branch_id, safeBranchData).replace(/"/g, '""')}"`,
                `"${formatDate(safeCustomerType.created_at).replace(/"/g, '""')}"`,
                `"${formatDate(safeCustomerType.updated_at).replace(/"/g, '""')}"`
            ];
        });

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `customer_types_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return { success: true };
    } catch (error) {
        console.error('Customer Types CSV export failed:', error);
        throw new Error('Failed to export to CSV: ' + error.message);
    }
};